import { lazy, useMemo } from "react";
import { Routes, Route, Outlet } from "react-router-dom";

import { Layout } from "@components/Layout";
import LayoutAuth from "@components/Layout/LayoutAuth";
import countryLangStore from "@zustand/countryLang";
import { checkCPI } from "@utils/manageValue";

// Lazy loaded pages
const Home = lazy(() => import("../pages/Home"));
const MenuHome = lazy(() => import("../pages/Menu"));
const Search = lazy(() => import("@pages/Search"));
const NotFound = lazy(() => import("@pages/NotFound"));
const ManageTransaction = lazy(() => import("@pages/ManageTransaction"));
const SellerTransport = lazy(() => import("@pages/SellerTransport"));
const MenuLogin = lazy(() => import("@pages/MenuLogin"));
const BarcodeSlip = lazy(() => import("@pages/BarcodeSlip"));
const PlanningList = lazy(() => import("@pages/Planning/PlanningList"));
const PlanningTable = lazy(() => import("@pages/Planning/PlanningTable"));
const ContractMain = lazy(() => import("@pages/ContractReport/ContractMain"));
const ContractReport = lazy(
  () => import("@pages/ContractReport/ContractReport")
);
const Login = lazy(() => import("@pages/Login"));
const ResetPassword = lazy(() => import("@pages/ResetPassword"));
const SellerTransportCPI = lazy(() => import("@pages/SellerTransportCPI"));
const RegisterDriver = lazy(() => import("@pages/RegisterDriver"));
const SellRecord = lazy(() => import("@pages/SellRecords"));

const AuthenticatedRoute = () => {
  return (
    <LayoutAuth>
      <Outlet />
    </LayoutAuth>
  );
};

const Router = () => {
  const storageUser = countryLangStore();
  const isCpi = checkCPI(storageUser?.user?.corporation_code);

  const { isCountry, isNotCheckLogin, isCheckLogin } = useMemo(() => {
    const isCheckCountry = storageUser?.country?.id === 0;
    return {
      isCountry: isCheckCountry,
      isCheckLogin: !isCheckCountry && storageUser?.user?.isLogin,
      isNotCheckLogin: !isCheckCountry && !storageUser?.user?.isLogin,
    };
  }, [storageUser?.country, storageUser?.user?.isLogin]);

  if (isCountry)
    return (
      <Routes>
        <Route
          path="*"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
      </Routes>
    );

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Layout back>
            <Login />
          </Layout>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Layout>
            <ResetPassword />
          </Layout>
        }
      />
      {isNotCheckLogin && (
        <Route>
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
          <Route
            path="/menu-login"
            element={
              <Layout>
                <MenuLogin />
              </Layout>
            }
          />
          <Route
            path="/search"
            element={
              <Layout>
                <Search />
              </Layout>
            }
          />
          <Route
            path="/register-driver-public"
            element={
              <Layout>
                <RegisterDriver />
              </Layout>
            }
          />
        </Route>
      )}
      {isCheckLogin && (
        <Route element={<AuthenticatedRoute />}>
          <Route path="/code-generate/:id" element={<BarcodeSlip />} />
          <Route path="/search" element={<Search />} />
          <Route path="/" element={<MenuHome />} />
          <Route path="/manage-transaction" element={<ManageTransaction />} />
          <Route path="/seller-record" element={<SellRecord />} />
          <Route path="/register-driver-public" element={<RegisterDriver />} />
          <Route
            path="/seller-transport"
            element={isCpi ? <SellerTransportCPI /> : <SellerTransport />}
          />
          {storageUser?.user?.grade === "a" && (
            <>
              <Route path="/planning" element={<PlanningList />} />
              <Route path="/planning/create" element={<PlanningTable />} />
              <Route path="/planning/detail/:id" element={<PlanningTable />} />
            </>
          )}
          <Route path="/contract" element={<ContractMain />} />
          <Route path="/contract/report" element={<ContractReport />} />
        </Route>
      )}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Router;
